import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { BaseOnlineCourseModeStrategyService } from '@lx/quote-tool/lite/online/services';
import { OnlineCourseDetails, RouteIds } from '../../../types';
import { OnlineQuoteToolState } from '../../../store';
import { StepsDefinitionService } from '../../steps-definition.service';
import { Course } from '@lx/shared/data-access/types';
import { CountryDetectorService } from '@lx/quote-tool/shared/services';

/**
 * Online Course Mode Strategy for Online Quote Tool
 *
 * This strategy handles deep link navigation with pre-filled course data.
 * It extends the shared BaseOnlineCourseModeStrategyService to reuse common logic
 * while providing Online QT-specific implementations.
 *
 * Deep Link Format:
 * ?quoteToolMode=online-course&schoolCode=OIE&courseCode=OLENG2&duration=4
 *  &classStartTime=1700&classEndTime=1915&startDate=2026-07-20&endDate=2026-08-14
 *
 * Behavior:
 * - Parses deep link parameters
 * - Loads course and school data
 * - Populates store with pre-filled selections
 * - ALWAYS navigates to step 1 (build-your-course)
 *
 * @example
 * // User clicks deep link with course pre-selected
 * loadData$() // Loads course data, saves to session storage
 * getStepToNavigate() // Returns RouteIds.buildYourCourse
 */
@Injectable({
  providedIn: 'root',
})
export class OnlineCourseModeStrategyService extends BaseOnlineCourseModeStrategyService<RouteIds, OnlineCourseDetails, OnlineQuoteToolState> {
  private readonly stepsDefinitionService = inject(StepsDefinitionService);
  private readonly countryDetectorService = inject(CountryDetectorService);

  /**
   * Returns the step to navigate to after loading deep link data
   *
   * For Online QT, deep links ALWAYS navigate to the first step (build-your-course),
   * regardless of the pre-filled data. This allows users to review the selections.
   *
   * @returns RouteIds.buildYourCourse (always first step for deep links)
   */
  getStepToNavigate(): Observable<RouteIds> | RouteIds {
    return of(RouteIds.buildYourCourse);
  }

  /**
   * Provides the service to detect user's residence country
   * Used by the base class to determine country-specific settings
   */
  protected getResidenceCountryCode(): string | undefined {
    return this.countryDetectorService.detectCountryCode();
  }

  /**
   * Builds the course details object from the provided course data
   *
   * This method extends the base implementation to include Online QT-specific fields,
   * such as benefits, which are not present in the base CourseDetails structure.
   * @param course - The course data extracted from deep link parameters
   * @returns OnlineCourseDetails object with additional fields
   * @protected
   */
  protected buildCourseDetails(course: Course): OnlineCourseDetails {
    const { name, shortName, benefits } = course;
    return {
      ...super.buildCourseDetails(course),
      shortName: shortName || name,
      benefits: benefits || [],
    };
  }

  protected getCurrentStepId(): RouteIds {
    return this.stepsDefinitionService.getStepDefinition(RouteIds.buildYourCourse).id;
  }
}
